import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '@/lib/api';

export interface DisparoLote {
  id: number;
  client_id: number;
  data_vencimento: string;
  tipo_envio: string;
  quantidade_total: number;
  quantidade_enviados: number;
  quantidade_pendentes: number;
  quantidade_falhas: number;
  quantidade_baixados: number;
  quantidade_excluidos: number;
  quantidade_invalidos: number;
  status: string;
  is_cancelled: boolean;
  cancelled_by_user_id?: number;
  cancelled_at?: string;
  created_at: string;
  updated_at: string;
  createdAt: string;
  updatedAt: string;
  vencimento: string;
  total_boletos: number;
  total_enviados: number;
  total_pendentes: number;
  total_fila: number;
  total_aguardando_confirmacao?: number;
  total_falhas: number;
  total_invalidos: number;
  total_bloqueados?: number;
  total_baixados: number;
  valor_total: number;
  nome?: string;
  tag?: string;
}

interface DisparosContextType {
  lotes: DisparoLote[];
  isLoading: boolean;
  loadLotes: () => Promise<void>;
  refreshLotes: () => Promise<void>;
  clearLotes: () => void;
  clienteId: number | null;
  statusFilter: string;
  setClienteId: (id: number | null) => void;
  setStatusFilter: (filter: string) => void;
}

const DisparosContext = createContext<DisparosContextType | undefined>(undefined);

export const useDisparos = () => {
  const context = useContext(DisparosContext);
  if (!context) {
    throw new Error('useDisparos deve ser usado dentro de DisparosProvider');
  }
  return context;
};

interface DisparosProviderProps {
  children: ReactNode;
  user: any;
  clienteId?: number | null;
  statusFilter?: string;
}

export const DisparosProvider: React.FC<DisparosProviderProps> = ({ 
  children, 
  user, 
  clienteId: initialClienteId = null, 
  statusFilter: initialStatusFilter = 'all' 
}) => {
  const [lotes, setLotes] = useState<DisparoLote[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clienteId, setClienteId] = useState<number | null>(initialClienteId);
  const [statusFilter, setStatusFilter] = useState<string>(initialStatusFilter);

  const loadLotes = async () => {
    console.log('🚀 [DisparosContext] loadLotes INICIADO');
    console.log('🔍 [DisparosContext] User disponível:', !!user);
    console.log('🔍 [DisparosContext] User details:', user ? { id: user.id, role: user.role, client_id: user.client_id } : 'null');
    
    if (!user) {
      console.log('⏳ [DisparosContext] Usuário não disponível - ABORTANDO');
      return;
    }

    try {
      console.log('🔄 [DisparosContext] Iniciando carregamento de lotes...');
      setIsLoading(true);
      // Limpar dados antigos antes de carregar novos
      setLotes([]);
      console.log('🗑️ [DisparosContext] Dados antigos limpos');
      
      const params: any = {};
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      
      // 🔧 FILTRO ÚLTIMOS 7 DIAS: Adicionar filtro de data para mostrar apenas lotes dos últimos 7 dias (incluindo hoje)
      // CORREÇÃO TIMEZONE: Usar componentes de data local diretamente para evitar problemas de timezone
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const todayStr = `${year}-${month}-${day}`; // YYYY-MM-DD no timezone local
      
      // Calcular 7 dias atrás no timezone local
      const sevenDaysAgoDate = new Date(now);
      sevenDaysAgoDate.setDate(sevenDaysAgoDate.getDate() - 6); // 6 dias atrás + hoje = 7 dias
      const yearAgo = sevenDaysAgoDate.getFullYear();
      const monthAgo = String(sevenDaysAgoDate.getMonth() + 1).padStart(2, '0');
      const dayAgo = String(sevenDaysAgoDate.getDate()).padStart(2, '0');
      const sevenDaysAgoStr = `${yearAgo}-${monthAgo}-${dayAgo}`; // YYYY-MM-DD no timezone local
      
      // Calcular dia seguinte para garantir que inclua lotes criados até o final do dia atual
      // mesmo que sejam salvos no dia seguinte em UTC (problema de timezone)
      const tomorrowDate = new Date(now);
      tomorrowDate.setDate(tomorrowDate.getDate() + 1);
      const yearTomorrow = tomorrowDate.getFullYear();
      const monthTomorrow = String(tomorrowDate.getMonth() + 1).padStart(2, '0');
      const dayTomorrow = String(tomorrowDate.getDate()).padStart(2, '0');
      const tomorrowStr = `${yearTomorrow}-${monthTomorrow}-${dayTomorrow}`; // YYYY-MM-DD do dia seguinte
      
      // Formatar datas no formato YYYY-MM-DD (timezone local)
      params.date_from = sevenDaysAgoStr;
      params.date_to = tomorrowStr; // Usar dia seguinte para incluir todo o dia atual
      
      console.log('📅 [DisparosContext] Filtro de data aplicado - últimos 7 dias (incluindo hoje):', { 
        from: params.date_from, 
        to: params.date_to,
        todayStr,
        sevenDaysAgoStr,
        tomorrowStr,
        now: now.toISOString(),
        nowLocal: `${year}-${month}-${day}`
      });
      
      // CORREÇÃO: Passar client_id corretamente baseado no tipo de usuário
      if (user?.role === 'admin') {
        // Admin DEVE ter clienteId selecionado para ver dados
        if (clienteId) {
          params.client_id = clienteId;
          console.log(`🔍 [DisparosContext] Admin filtrando por client_id: ${clienteId}`);
        } else {
          console.log(`🔍 [DisparosContext] Admin sem cliente selecionado - não fazendo requisição`);
          setLotes([]);
          setIsLoading(false);
          return;
        }
      } else if (user?.role === 'cliente' && user.client_id) {
        // Cliente sempre usa seu próprio client_id
        params.client_id = user.client_id;
        console.log(`🔍 [DisparosContext] Cliente usando client_id: ${user.client_id}`);
      }
      
      console.log('🔄 [DisparosContext] Parâmetros finais:', params);
      console.log('🔍 [DisparosContext] URL completa:', '/disparos/lotes');
      console.log('🔍 [DisparosContext] User info:', { role: user?.role, client_id: user?.client_id });
      console.log('🌐 [DisparosContext] API Base URL:', import.meta.env.VITE_API_BASE_URL);
      console.log('🌐 [DisparosContext] URL completa da requisição:', `${import.meta.env.VITE_API_BASE_URL || '/api'}/disparos/lotes`);
      
      console.log('📡 [DisparosContext] Fazendo requisição para API...');
      const response = await api.get('/disparos/lotes', { params });
      console.log('✅ [DisparosContext] Resposta recebida da API');
      console.log('📊 [DisparosContext] Resposta completa:', response);
      console.log('🔍 [DisparosContext] Tipo da resposta:', typeof response);
      console.log('🔍 [DisparosContext] Keys da resposta:', Object.keys(response || {}));
      
      const lotesData = response.lotes || [];
      console.log('📊 [DisparosContext] Lotes extraídos:', lotesData.length);
      console.log('📊 [DisparosContext] Lotes detalhados:', lotesData.map(l => ({ id: l.id, status: l.status, nome: l.nome, total_boletos: l.total_boletos })));
      
      console.log('💾 [DisparosContext] Atualizando estado com lotes...');
      setLotes(lotesData);
      console.log('✅ [DisparosContext] Estado atualizado com sucesso');
    } catch (error) {
      console.error('❌ [DisparosContext] ERRO ao carregar lotes:', error);
      console.error('❌ [DisparosContext] Tipo do erro:', typeof error);
      console.error('❌ [DisparosContext] Stack do erro:', error);
    } finally {
      console.log('🏁 [DisparosContext] Finalizando carregamento...');
      setIsLoading(false);
      console.log('✅ [DisparosContext] loadLotes CONCLUÍDO');
    }
  };

  const refreshLotes = async () => {
    console.log('🔄 [DisparosContext] Refresh solicitado');
    await loadLotes();
  };

  const clearLotes = () => {
    console.log('🗑️ [DisparosContext] Limpando lotes');
    setLotes([]);
    // Limpar cache do localStorage
    localStorage.removeItem('disparos_lotes');
    localStorage.removeItem('dashboard_stats');
  };

  // CORREÇÃO: Definir clienteId automaticamente para clientes
  useEffect(() => {
    if (user?.role === 'cliente' && user.client_id && !clienteId) {
      console.log('🔄 [DisparosContext] Definindo clienteId automaticamente para cliente:', user.client_id);
      setClienteId(user.client_id);
    }
  }, [user, clienteId]);

  // CORREÇÃO: Carregar lotes quando as dependências mudarem
  useEffect(() => {
    console.log('🔄 [DisparosContext] useEffect executado', { user: !!user, clienteId, statusFilter, isLoading });
    console.log('🔄 [DisparosContext] Dependências mudaram - clienteId:', clienteId, 'statusFilter:', statusFilter);
    
    if (user) {
      // CORREÇÃO: Sempre limpar dados antigos primeiro
      console.log('🔄 [DisparosContext] Limpando dados antigos...');
      setLotes([]);
      
      // Aguardar um pouco para garantir que a limpeza foi processada
      const timer = setTimeout(() => {
        if (user?.role === 'admin') {
          // Admin só carrega dados se tiver um cliente selecionado
          if (clienteId) {
            console.log('🔄 [DisparosContext] Admin - carregando dados para cliente:', clienteId);
            loadLotes();
          } else {
            console.log('🔄 [DisparosContext] Admin - nenhum cliente selecionado, limpando dados');
            setLotes([]);
          }
        } else if (user?.role === 'cliente' && user.client_id) {
          // Cliente sempre vê apenas seus próprios dados
          console.log('🔄 [DisparosContext] Cliente - carregando dados próprios');
          loadLotes();
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [user, clienteId, statusFilter]);

  // CORREÇÃO: Removido useEffect duplicado que causava conflitos

  // CORREÇÃO: Removido event listener de focus que causava recarregamentos desnecessários
  // O recarregamento automático estava causando zeramento dos dados quando a página perdia/ganhava foco

  const value: DisparosContextType = {
    lotes,
    isLoading,
    loadLotes,
    refreshLotes,
    clearLotes,
    clienteId,
    statusFilter,
    setClienteId,
    setStatusFilter
  };

  return (
    <DisparosContext.Provider value={value}>
      {children}
    </DisparosContext.Provider>
  );
};
